import {AfterViewInit, Component, Inject, Input, OnInit, ViewChild} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {MatSort} from '@angular/material/sort';
import {formatDate} from '@angular/common';
import {AppLocaleService} from '@core/services/app-locale.service';
import {CassError} from '@core/models/cass-error';
import {BehaviorSubject, distinctUntilChanged} from 'rxjs';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {SubSink} from 'subsink';
import {OrdersService} from '@core/services/orders.service';
import {UserAlertService} from '@shared/services/user-alert.service';
import {OrderHistoryRecord} from '@core/models/order-history-record';
import {ComponentPortal} from '@angular/cdk/portal';
import {
	IndeterminateHorizontalProgressIndicatorComponent
} from '@shared/components/indeterminate-horizontal-progress-indicator/indeterminate-horizontal-progress-indicator.component';
import {TranslateService} from '@ngx-translate/core';
import {MatTableDataSource} from '@angular/material/table';

@Component({
  selector: 'app-order-history-dialog',
  templateUrl: './order-history-dialog.component.html',
  styleUrls: ['./order-history-dialog.component.css']
})
export class OrderHistoryDialogComponent implements OnInit, AfterViewInit{

	@Input()
	orderId: string | undefined = undefined

	orderHistory: OrderHistoryRecord[] = [];

	orderHistoryTableDataSource = new MatTableDataSource<OrderHistoryRecord>();

	@ViewChild('orderHistoryTable', {read: MatSort}) orderHistoryTableSort: MatSort | null = null;
	orderHistoryTableColumnDefs: any[] = [
		{
			key: 'timestamp',
			heading: 'ORDER_BASKET.HISTORY_DIALOG.TABLE_COLUMN.TIMESTAMP',
			displayValue: (data: OrderHistoryRecord): string => formatDate(data.timestamp || '', this._localeService.timestampFormat, this._localeService.currentOrDefaultLocaleId),
			sortableValue: (data: OrderHistoryRecord): number => (new Date(data.timestamp || '')).valueOf()
		},
		{
			key: 'changedBy',
			heading: 'ORDER_BASKET.HISTORY_DIALOG.TABLE_COLUMN.CHANGED_BY',
			displayValue: (data: OrderHistoryRecord): string => this.getChangedByValue(data)
		},
		{
			key: 'change',
			heading: 'ORDER_BASKET.HISTORY_DIALOG.TABLE_COLUMN.CHANGE',
			displayValue: (data: OrderHistoryRecord): string => this.getHistoryValue(data),
		},
		{
			key: 'oldValue',
			heading: 'ORDER_BASKET.HISTORY_DIALOG.TABLE_COLUMN.OLD',
			displayValue: (data: OrderHistoryRecord): string => data.oldValue,
		},
		{
			key: 'newValue',
			heading: 'ORDER_BASKET.HISTORY_DIALOG.TABLE_COLUMN.NEW',
			displayValue: (data: OrderHistoryRecord): string => data.newValue,
		},
	];

	orderHistoryTableDisplayedColumns = [...this.orderHistoryTableColumnDefs.map(x => x.key)];


	private readonly _callingOut$ = new BehaviorSubject<boolean>(false);
	private _fullscreenProgressOverlayRef?: OverlayRef;
	private subs = new SubSink();

	constructor(
		private _dialogRef: MatDialogRef<OrderHistoryDialogComponent>,
		private _localeService: AppLocaleService,
		private _orderService: OrdersService,
		private _alertService: UserAlertService,
		private _overlay: Overlay,
		private _translate: TranslateService,
	) {
	}

	ngOnInit(): void {
		console.log(this.orderId);
		this._fullscreenProgressOverlayRef = this._overlay.create({
			positionStrategy: this._overlay.position().global().centerHorizontally().centerVertically(),
			hasBackdrop: true
		});

		this.subs.add(
			this._callingOut$.pipe(distinctUntilChanged()).subscribe(show => {
				if (show) {
					const componentRef = this._fullscreenProgressOverlayRef?.attach(new ComponentPortal(IndeterminateHorizontalProgressIndicatorComponent));
					if (componentRef) {
						this._translate.get('APP_NOTIFICATIONS.PLEASE_WAIT_MESSAGE').subscribe((msg) => componentRef.instance.displayString = msg);
					}
				} else {
					this._fullscreenProgressOverlayRef?.detach();
				}
			}));

		if (this.orderId) {
			this.getOrderHistory(this.orderId)
		}
	}

	ngAfterViewInit(): void {
		this.updateOrderHistoryDataSource(this.orderHistory);
	}

	closeDialog() {
		this._dialogRef.close();
	}

	ngOnDestroy(): void {
		this.subs.unsubscribe();
		if (this._fullscreenProgressOverlayRef && this._fullscreenProgressOverlayRef.hasAttached()) {
			this._fullscreenProgressOverlayRef.detach();
		}
	}

	private getHistoryValue(data: OrderHistoryRecord) {
		return data.historyType && data.historySubtype ? data.historyType + ': ' + data.historySubtype : '';
	}

	private updateOrderHistoryDataSource(orderHistory: OrderHistoryRecord[]) {
		this.orderHistoryTableDataSource.data = orderHistory;
		this.orderHistoryTableDataSource.sort = this.orderHistoryTableSort;
		this.orderHistoryTableDataSource.sortingDataAccessor = (item: OrderHistoryRecord, sortHeaderId: string) => {
			const columnDef = this.orderHistoryTableColumnDefs.find(x => x.key === sortHeaderId);
			if (columnDef) {
				return columnDef.sortableValue ? columnDef.sortableValue(item) : columnDef.displayValue ? columnDef.displayValue(item) : (item as any)[sortHeaderId] ?? '';
			}
		};
	}

	private getOrderHistory(orderId: string) {
		this._callingOut$.next(true);

		this.subs.add(
			this._orderService.getOrderHistoryById(orderId)
				.subscribe({
					next: (res: OrderHistoryRecord[]) => {
						this._callingOut$.next(false);
						this.orderHistory = res;
						this.orderHistoryTableDataSource.data = this.orderHistory;
					},
					error: (err: CassError) => {
						this._callingOut$.next(false);
						this._alertService.errorByCassError(err);
						this.closeDialog();
					},
				})
		)
	}

	private getChangedByValue(data: OrderHistoryRecord) {
		return data.username && data.externalCompany ? data.username + ' ' + data.externalCompany : data.username || '';
	}
}
